import React from 'react';
import { getPricePollingService, type PricePollingEvent } from '../services/pricePollingService';
import type { Ticker } from '../types/mexc';

export type PricePollingContextState = {
    // Polling state
    isPolling: boolean;
    lastPollTime: number;
    nextPollTime: number;
    timeUntilNextPoll: number;
    pollIntervalMs: number;

    // Ticker data
    tickers: Record<string, Ticker>;

    // Error state
    lastError: string | null;

    // Actions
    forcePoll: () => Promise<void>;
};

const PricePollingContext = React.createContext<PricePollingContextState | null>(null);

export function PricePollingProvider({ children }: { children: React.ReactNode }) {
    const service = React.useMemo(() => getPricePollingService(), []);

    const [state, setState] = React.useState({
        isPolling: service.isServiceRunning(),
        lastPollTime: service.getLastPollTime(),
        nextPollTime: service.getNextPollTime(),
        timeUntilNextPoll: service.getTimeUntilNextPoll(),
        pollIntervalMs: service.getPollIntervalMs(),
        tickers: service.getAllTickers(),
        lastError: null as string | null,
    });

    // Start polling service on mount
    React.useEffect(() => {
        if (!service.isServiceRunning()) {
            service.start();
        }

        // Event handler for polling updates
        const handleEvent = (event: PricePollingEvent) => {
            switch (event.type) {
                case 'poll_start':
                    setState(prev => ({ ...prev, isPolling: true }));
                    break;
                case 'poll_complete':
                    setState(prev => ({
                        ...prev,
                        isPolling: false,
                        lastPollTime: service.getLastPollTime(),
                        nextPollTime: service.getNextPollTime(),
                        tickers: service.getAllTickers(),
                        lastError: null,
                    }));
                    break;
                case 'poll_error':
                    setState(prev => ({
                        ...prev,
                        isPolling: false,
                        lastError: event.data?.error || 'Unknown error',
                    }));
                    break;
                case 'price_update':
                    // Individual price updates - batch these by not updating on every tick
                    // The poll_complete event will provide the full update
                    break;
            }
        };

        service.on(handleEvent);

        return () => {
            service.off(handleEvent);
            // Don't stop the service on unmount - it's a singleton
        };
    }, [service]);

    // Update countdown timer every second
    React.useEffect(() => {
        const intervalId = setInterval(() => {
            setState(prev => ({
                ...prev,
                timeUntilNextPoll: service.getTimeUntilNextPoll(),
            }));
        }, 1000);

        return () => clearInterval(intervalId);
    }, [service]);

    const forcePoll = React.useCallback(async () => {
        await service.forcePoll();
    }, [service]);

    const contextValue: PricePollingContextState = React.useMemo(() => ({
        isPolling: state.isPolling,
        lastPollTime: state.lastPollTime,
        nextPollTime: state.nextPollTime,
        timeUntilNextPoll: state.timeUntilNextPoll,
        pollIntervalMs: state.pollIntervalMs,
        tickers: state.tickers,
        lastError: state.lastError,
        forcePoll,
    }), [state, forcePoll]);

    return (
        <PricePollingContext.Provider value={contextValue}>
            {children}
        </PricePollingContext.Provider>
    );
}

export function usePricePollingContext() {
    const ctx = React.useContext(PricePollingContext);
    if (!ctx) {
        throw new Error('usePricePollingContext must be used within PricePollingProvider');
    }
    return ctx;
}
